import { FormControl, FormHelperText, FormLabel, Input, Tooltip } from '@mui/joy';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { getIn, useFormikContext } from 'formik';
import classNames from 'classnames';

import { InfoIcon } from '@/shared/icons/Icons';

// Define the base props without the type and onChangeCallback
type BaseProps = {
  label?: string;
  name: string;
  required?: boolean;
  getMaskedValue?: (value: string) => string;
  setMaskedValue?: (value: string) => string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  max?: number;
  min?: number;
  fullWidth?: boolean;
  infoTooltip?: string;
} & Omit<React.ComponentProps<typeof Input>, 'type' | 'onChange'>;

interface TextOrPasswordProps extends BaseProps {
  type?: 'text' | 'password';
  onChangeCallback?: (value: string) => void;
}

interface NumberProps extends BaseProps {
  type: 'number';
  onChangeCallback?: (value: number) => void;
}

type Props = TextOrPasswordProps | NumberProps;

const FormikTextField = forwardRef<{ focus: () => void }, Props>(
  (
    {
      className,
      disabled,
      getMaskedValue,
      label,
      name,
      onBlur,
      onChangeCallback,
      required = false,
      setMaskedValue,
      type = 'text',
      min,
      max,
      fullWidth = true,
      infoTooltip,
      ...rest
    },
    ref,
  ) => {
    const { errors, touched, values, setFieldValue, setFieldTouched } = useFormikContext();
    const textFieldRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          (textFieldRef.current?.firstChild as HTMLElement).focus();
        },
      }),
      [],
    );

    const error = getIn(errors, name);
    const hasError = !!(getIn(touched, name) && error);

    const labelComponent = useMemo(() => {
      if (!label) {
        return null;
      }

      if (infoTooltip) {
        return (
          <div className="flex">
            <FormLabel required={required}>{label}</FormLabel>
            <Tooltip title={infoTooltip}>
              <i className="ml-3 cursor-pointer text-sm">
                <InfoIcon />
              </i>
            </Tooltip>
          </div>
        );
      }

      return <FormLabel required={required}>{label}</FormLabel>;
    }, [label, required, infoTooltip]);

    return (
      <FormControl
        className={classNames(className, { 'min-w-0 flex-1': fullWidth })}
        error={hasError}
      >
        {labelComponent}
        <Input
          className={classNames({ 'Mui-disabled': disabled })}
          size="sm"
          ref={textFieldRef}
          name={name}
          value={getMaskedValue ? getMaskedValue(getIn(values, name)) : getIn(values, name) ?? ''}
          fullWidth
          type={type}
          onChange={async ({ target }) => {
            let { value } = target;

            setFieldTouched(name, true);

            if (type === 'number') {
              const numberValue = parseFloat(value) ?? 0;

              if (max != null && numberValue > max) {
                await setFieldValue(name, max);
                (onChangeCallback as (value: number) => void | undefined)?.(max);
                return;
              }
              if (min != null && numberValue < min) {
                await setFieldValue(name, min);
                (onChangeCallback as (value: number) => void | undefined)?.(min);
                return;
              }
              await setFieldValue(name, numberValue);
              (onChangeCallback as (value: number) => void | undefined)?.(numberValue);
              return;
            }

            if (setMaskedValue) {
              value = setMaskedValue(value);
            }
            await setFieldValue(name, value);
            (onChangeCallback as (value: string) => void | undefined)?.(value);
          }}
          onBlur={(e) => {
            onBlur?.(e);
            setFieldTouched(name, true);
          }}
          slotProps={{
            input: {
              disabled,
              readOnly: disabled,
            },
          }}
          {...rest}
        />
        {hasError && <FormHelperText className="pl-1 text-xs">{error}</FormHelperText>}
      </FormControl>
    );
  },
);

export default FormikTextField;
